import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../../../services/baseQuery';
import type { CommentDto, CommentInsertDto, MentionUser } from '../types';
import { COMMENTS_API, GRID_API } from '../../../services/apiPaths';
import { normalizeMediaBeforeSave } from '../../detail-form/richTextMediaHelpers';

export const commentsApi = createApi({
  reducerPath: 'commentsApi',
  baseQuery: baseQuery,
  tagTypes: ['Comment', 'Employee'],

  endpoints: (builder) => ({
    getComments: builder.query<CommentDto[], { entityId: number; entityTypeId: number }>({
      query: (params) => ({
        url: `${COMMENTS_API}/GetComments`,
        params,
      }),
      transformResponse: (response: CommentDto[] | unknown): CommentDto[] => {
        if (Array.isArray(response)) {
          return response as CommentDto[];
        }
        throw new Error('Failed to load comments: unexpected response shape');
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ CommentId }) => ({ type: 'Comment' as const, id: CommentId })),
              { type: 'Comment', id: 'LIST' },
            ]
          : [{ type: 'Comment', id: 'LIST' }],
    }),
    addComment: builder.mutation<{ code: number; message: string }, CommentInsertDto>({
      query: (body) => ({
        url: `${COMMENTS_API}/AddComment`,
        method: 'POST',
        body: { ...body, CommentContent: normalizeMediaBeforeSave(body.CommentContent) },
      }),
      invalidatesTags: () => [{ type: 'Comment', id: 'LIST' }],
    }),
    editComment: builder.mutation<
      { code: number; data: CommentDto; message: string },
      CommentInsertDto & { CommentId: number }
    >({
      query: (body) => ({
        url: `${COMMENTS_API}/EditComment`,
        method: 'PUT',
        body: { ...body, CommentContent: normalizeMediaBeforeSave(body.CommentContent) },
      }),
      invalidatesTags: (_result, _error, { CommentId }) => [{ type: 'Comment', id: CommentId }],
    }),
    deleteComment: builder.mutation<{ code: number; message: string }, number>({
      query: (id) => ({
        url: `${COMMENTS_API}/DeleteComment`,
        method: 'DELETE',
        params: { commentId: id },
      }),
      invalidatesTags: (_result, _error, id) => [{ type: 'Comment', id }],
    }),
    getEmployees: builder.query<MentionUser[], void>({
      query: () => ({ url: `${GRID_API}/GetAllEmployees` }),
      transformResponse: (raw: { Id: number; Name: string; Initials?: string }[]) =>
        raw.map((u) => ({ id: u.Id.toString(), name: u.Name, initials: u.Initials })),
      providesTags: [{ type: 'Employee', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetCommentsQuery,
  useAddCommentMutation,
  useEditCommentMutation,
  useDeleteCommentMutation,
  useGetEmployeesQuery,
} = commentsApi;
